import "./toolbar.css";

import { mm } from "@inovitas/infra3dsdk";
import { Box } from "@mui/material";
import React, { useEffect } from "react";

import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore, Topics } from "shared/common/stores/sidebarStore";
import { useTranslation } from "react-i18next";
import {
  getLayersAddedByUser,
  saveLayersToLocalStorage,
} from "shared/common/utils/layers/localStorageLayers";

interface props {
  children: JSX.Element | JSX.Element[];
}

export default function Toolbar({ children }: props) {
  const viewer = defaultStore((state) => state.viewer);
  const showStreetMap = defaultStore((state) => state.showStreetMap);
  const showAxisscale = defaultStore((state) => state.showAxisscale);
  const authMode = defaultStore((state) => state.authMode);
  const changeAlertMessage = defaultStore((state) => state.changeAlertMessage);

  const imagePopover = sidebarStore((state) => state.imagePopover);
  const topicPopover = sidebarStore((state) => state.topicPopover);
  const infosPopover = sidebarStore((state) => state.infosPopover);
  const measurementPopover = sidebarStore((state) => state.measurementPopover);
  const editPopover = sidebarStore((state) => state.editPopover);
  const changeTopics = sidebarStore((state) => state.changeTopics);
  const layerGroups = sidebarStore((state) => state.layerGroups);
  const changeLayerGroups = sidebarStore((state) => state.changeLayerGroups);
  const selectTools = sidebarStore((state) => state.selectTools);
  const showDescription = sidebarStore((state) => state.showDescription);
  const changeUpdateFeaturesList = sidebarStore(
    (state) => state.changeUpdateFeaturesList
  );
  const maximizeTable = sidebarStore((state) => state.maximizeTable);
  const axisControlPopover = sidebarStore((state) => state.axisControlPopover);

  const { t } = useTranslation();

  //enable or disable select tool
  useEffect(() => {
    if (selectTools !== undefined) {
      if (infosPopover) {
        selectTools.enable();
        const axisPickTool = sidebarStore.getState().axisPickTool;
        if (axisPickTool && axisPickTool.enabled) axisPickTool.disable();
      } else {
        const activelayer = viewer
          ?.getScene()
          .getLayerHandler()
          .getActiveLayer();
        if (activelayer) {
          activelayer.featureselector.clearSelection();
        }
        selectTools.disable();
        const axisPickTool = sidebarStore.getState().axisPickTool;
        if (axisPickTool && !axisPickTool.enabled) axisPickTool.enable();
      }
    }
  }, [infosPopover]);

  //update layers list and group wile chaning a layer
  const onLayersChanged = () => {
    if (viewer === undefined) return;
    const layersArray: Topics[] = [];
    const groups: string[] = layerGroups;
    const visibleLayers = viewer
      .getScene()
      .getLayerHandler()
      .layers.filter((layer: mm.Layer) => !layer.options.hideinlayertree);

    visibleLayers.forEach((layer: mm.Layer) => {
      layersArray.push({
        addedFromUser: layer.addedFromUser,
        name: layer.layername,
        title: layer.title,
        folder: false,
        showOnMap: layer.getVisible(),
        icon: layer.options.geometrytype,
        layer,
      });

      if (
        layer.addedFromUser &&
        layer.topic !== "" &&
        !groups.includes(layer.topic)
      ) {
        groups.push(layer.topic);
      }
    });
    changeTopics(layersArray);
    changeLayerGroups(groups);
  };

  //update layer
  const updateMemoryLayer = (layer: any, changes: any) => {
    // TODO: Type (mm.Layer)
    if (layer.type != mm.LAYERTYPE.MEMORYLAYER) return;
    if (!changes.added && !changes.updated && !changes.removed) return;
    if (layer.addedFromUser) {
      const allLayersAddedFromUser = getLayersAddedByUser();
      allLayersAddedFromUser.forEach((item: any) => {
        item.layers.forEach((ls: any) => {
          if (
            (ls.layername === layer.layername || ls.title === layer.title) &&
            ls.geometrytype === layer.geometrytype
          ) {
            if (layer.data) {
              ls.data = layer.data;
            }
          }
        });
      });
      saveLayersToLocalStorage(allLayersAddedFromUser);
    }
  };

  //update feature table rows from after layer saved
  const updateLayers = () => {
    changeUpdateFeaturesList(true);
  };

  // add topics list from viewer
  useEffect(() => {
    let layer_added_nr: number;
    let layer_removed_nr: number;
    let layer_updated_nr: number;
    let layer_saved_nr: number;
    if (viewer !== undefined) {
      onLayersChanged();
      layer_added_nr = viewer
        .getScene()
        .userInputHandler.subscribeEvent(
          mm.USERINPUTTYPE.LAYERADDED,
          onLayersChanged,
          undefined
        );
      layer_removed_nr = viewer
        .getScene()
        .userInputHandler.subscribeEvent(
          mm.USERINPUTTYPE.LAYERREMOVED,
          onLayersChanged,
          undefined
        );
      layer_updated_nr = viewer
        .getScene()
        .userInputHandler.subscribeEvent(
          mm.USERINPUTTYPE.LAYERUPDATED,
          updateMemoryLayer,
          undefined
        );
      layer_saved_nr = viewer
        .getScene()
        .userInputHandler.subscribeEvent(
          mm.USERINPUTTYPE.LAYERSAVED,
          updateLayers,
          undefined
        );
    }
    return () => {
      if (layer_added_nr && viewer !== undefined)
        viewer
          .getScene()
          .userInputHandler.unSubscribeEvent(
            mm.USERINPUTTYPE.LAYERADDED,
            layer_added_nr
          );
      if (layer_removed_nr && viewer !== undefined)
        viewer
          .getScene()
          .userInputHandler.unSubscribeEvent(
            mm.USERINPUTTYPE.LAYERREMOVED,
            layer_removed_nr
          );
      if (layer_updated_nr && viewer !== undefined)
        viewer
          .getScene()
          .userInputHandler.unSubscribeEvent(
            mm.USERINPUTTYPE.LAYERUPDATED,
            layer_updated_nr
          );
      if (layer_saved_nr && viewer !== undefined)
        viewer
          .getScene()
          .userInputHandler.unSubscribeEvent(
            mm.USERINPUTTYPE.LAYERSAVED,
            layer_saved_nr
          );
    };
  }, [viewer]);

  const toolbarToolsFullHeight =
    maximizeTable ||
    (showDescription && measurementPopover) ||
    topicPopover ||
    axisControlPopover ||
    editPopover;

  const toolbarToolViewHeight =
    topicPopover || imagePopover || axisControlPopover;

  let minHeight = "0px";
  if (toolbarToolsFullHeight) {
    minHeight = "calc(100% - 32px)";
  } else if (toolbarToolViewHeight) {
    if (authMode === "view") {
      minHeight = "396px";
    } else {
      minHeight = showAxisscale ? "444px" : "396px";
    }
  }

  return (
    <Box
      paddingX={1}
      paddingY={2}
      display={showStreetMap ? "none" : "flex"}
      flexDirection="column"
      gap={1}
      sx={{
        width: "48px",
        minHeight: minHeight,
        transition: "min-height 0.3s",
        backgroundColor: "darkblue.main",
        borderRadius: "0px 10px 10px 0px",
        pointerEvents: "auto",
      }}
    >
      {children}
    </Box>
  );
}
